// src/pages/CRM/Core/ContactDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Breadcrumbs,
  Link,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Language as WebsiteIcon,
  Add as AddIcon,
  Note as NoteIcon,
  Event as MeetingIcon,
  PhoneCallback as CallIcon,
  Sms as SmsIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  Cake as CakeIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';
import ContactRelationships from './components/ContactRelationships';
import ContactGroupManager from './components/ContactGroupManager';
import ServiceReminders from './components/ServiceReminders';
import { Settings as SettingsIcon } from '@mui/icons-material';
import {
  ContactStatus,
  ContactStatusLabels,
  ContactStatusColors,
  LeadSource,
  LeadSourceLabels,
  ActivityType,
  ActivityTypeLabels,
} from './crmEnums';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contact-tabpanel-${index}`}
      aria-labelledby={`contact-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ContactDetail = () => {
  const navigate = useNavigate();
  const { chatbotId, contactId } = useParams();
  const [searchParams] = useSearchParams();
  const initialEditMode = searchParams.get('edit') === 'true';

  // Permission state (FIXED: Changed from direct assignment to state)
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [permissionsChecked, setPermissionsChecked] = useState(false);

  // State
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(initialEditMode);
  const [tabValue, setTabValue] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [tags, setTags] = useState([]);

  // Form state
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  const [convertResult, setConvertResult] = useState(null);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [deleteActivityId, setDeleteActivityId] = useState(null);
  const [activityForm, setActivityForm] = useState({
    activity_type: ActivityType.NOTE,
    subject: '',
    description: '',
    activity_date: new Date().toISOString().slice(0, 16),
    // Type-specific fields
    email_from: '',
    email_to: '',
    email_cc: '',
    call_direction: 'outbound',
    call_result: '',
    duration_minutes: '',
    task_due_date: '',
    task_priority: 'medium',
  });

  const [groups, setGroups] = useState([]);
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);

  // Check permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('crm', 'view');
      const edit = await hasPermission('crm', 'edit');
      const del = await hasPermission('crm', 'delete');
      
      setCanView(view);
      setCanEdit(edit);
      setCanDelete(del);
      setPermissionsChecked(true);
    };
    checkPermissions();
  }, []);

  // Fetch contact details
  const fetchContact = useCallback(async () => {
    // Wait for permissions to be checked
    if (!permissionsChecked) return;
    if (!canView) {
        setLoading(false);
        return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(`/api/crm/contacts/${contactId}`);

      if (response.data.success) {
        const contactData = response.data.data; // Note: API returns 'data' usually, check controller
        // Based on previous controller code: return {'success': True, 'data': contact.to_dict(...)}
        // Adjusting to match probable backend response structure
        const data = response.data.data || response.data.contact;
        
        setContact(data);
        setFormData({
          contact_type: data.contact_type || '',
          prefix: data.prefix || '',
          first_name: data.first_name || '',
          middle_name: data.middle_name || '',
          last_name: data.last_name || '',
          suffix: data.suffix || '',
          nickname: data.nickname || '',
          email: data.email || '',
          email_secondary: data.email_secondary || '',
          phone: data.phone || '',
          phone_mobile: data.phone_mobile || '',
          phone_work: data.phone_work || '',
          fax: data.fax || '',
          job_title: data.job_title || '',
          department: data.department || '',
          contact_group_id: data.contact_group_id || '',
          account_id: data.account_id || '',
          street: data.address?.street || '',
          city: data.address?.city || '',
          state: data.address?.state || '',
          postal_code: data.address?.postal_code || '',
          country: data.address?.country || '',
          linkedin_url: data.social?.linkedin_url || '',
          twitter_handle: data.social?.twitter_handle || '',
          facebook_url: data.social?.facebook_url || '',
          website: data.social?.website || '',
          date_of_birth: data.date_of_birth || '',
          description: data.description || '',
          status: data.status || ContactStatus.ACTIVE,
          lead_source: data.lead_source || '',
          email_opt_out: data.email_opt_out || false,
          sms_opt_out: data.sms_opt_out || false,
          do_not_call: data.do_not_call || false,
          tags: data.tags || [],
        });
      } else {
        setError(response.data.message || 'Failed to fetch contact');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch contact');
    } finally {
      setLoading(false);
    }
  }, [permissionsChecked, canView, contactId]);

  // Fetch accounts
  const fetchAccounts = async () => {
    try {
      const response = await axiosInstance.get('/api/crm/accounts?per_page=100');
      if (response.data.success) {
        setAccounts(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  // Fetch tags
  const fetchTags = async () => {
    try {
      const response = await axiosInstance.get('/api/crm/tags');
      if (response.data.success) {
        setTags(response.data.data?.filter((t) => t.applies_to_contacts) || []);
      }
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  };

  const fetchGroups = async () => {
      try {
          const res = await axiosInstance.get('/api/crm/contact-groups');
          if (res.data.success) setGroups(res.data.data);
      } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (permissionsChecked && canView) {
        fetchContact();
        fetchAccounts();
        fetchTags();
        fetchGroups();
    } else if (permissionsChecked && !canView) {
        setLoading(false);
    }
  }, [fetchContact, permissionsChecked, canView]);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFormChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleTagToggle = (tagName) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagName)
        ? prev.tags.filter((t) => t !== tagName)
        : [...prev.tags, tagName],
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name?.trim()) {
      errors.first_name = 'First name is required';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        account_id: formData.account_id || null, // Handle empty string
        address_street: formData.street,
        address_city: formData.city,
        address_state: formData.state,
        address_postal_code: formData.postal_code,
        address_country: formData.country,
      };

      const response = await axiosInstance.put(
        `/api/crm/contacts/${contactId}`,
        payload
      );

      if (response.data.success) {
        setEditMode(false);
        fetchContact();
      } else {
        setFormErrors({ submit: response.data.message || 'Failed to update contact' });
      }
    } catch (err) {
      setFormErrors({
        submit: err.response?.data?.message || 'Failed to update contact',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    // Reset form to original data (simple re-fetch logic for now)
    fetchContact();
    setFormErrors({});
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axiosInstance.delete(`/api/crm/contacts/${contactId}`);

      if (response.data.success) {
        navigate(`/chatbot/${chatbotId}/crm/contacts`);
      } else {
        setError(response.data.message || 'Failed to delete contact');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete contact');
    }
    setDeleteDialogOpen(false);
  };

  // Convert to Customer handler
  const handleConvertToCustomer = async () => {
    setConvertLoading(true);
    try {
      const response = await axiosInstance.post(`/api/crm/contacts/${contactId}/convert-to-customer`);
      if (response.data.success) {
        setConvertResult(response.data.data);
        setContact(response.data.data.contact);
      } else {
        setError(response.data.message || 'Failed to convert contact');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to convert contact to customer');
    }
    setConvertLoading(false);
    setConvertDialogOpen(false);
  };

  // Activity handlers
  const handleActivityFormChange = (field) => (event) => {
    setActivityForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const resetActivityForm = () => {
    setActivityForm({
      activity_type: ActivityType.NOTE,
      subject: '',
      description: '',
      activity_date: new Date().toISOString().slice(0, 16),
      email_from: '', email_to: '', email_cc: '',
      call_direction: 'outbound', call_result: '', duration_minutes: '',
      task_due_date: '', task_priority: 'medium',
    });
    setEditingActivity(null);
  };

  const handleEditActivity = (activity) => {
    setEditingActivity(activity);
    setActivityForm({
      activity_type: activity.activity_type || ActivityType.NOTE,
      subject: activity.subject || '',
      description: activity.description || '',
      activity_date: activity.activity_date
        ? new Date(activity.activity_date).toISOString().slice(0, 16) : '',
      email_from: activity.email_from || '',
      email_to: activity.email_to || '',
      email_cc: activity.email_cc || '',
      call_direction: activity.call_direction || 'outbound',
      call_result: activity.call_result || '',
      duration_minutes: activity.duration_minutes || '',
      task_due_date: activity.task_due_date
        ? new Date(activity.task_due_date).toISOString().slice(0, 16) : '',
      task_priority: activity.task_priority || 'medium',
    });
    setActivityDialogOpen(true);
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      const response = await axiosInstance.delete(`/api/crm/activities/${activityId}`);
      if (response.data.success) {
        fetchContact();
      }
    } catch (err) {
      console.error('Failed to delete activity:', err);
    }
    setDeleteActivityId(null);
  };

  const handleAddActivity = async () => {
    try {
      const payload = {
        activity_type: activityForm.activity_type,
        subject: activityForm.subject,
        description: activityForm.description,
        activity_date: activityForm.activity_date,
        contact_id: parseInt(contactId),
      };

      // Add type-specific fields
      if (activityForm.activity_type === ActivityType.EMAIL) {
        payload.email_from = activityForm.email_from;
        payload.email_to = activityForm.email_to;
        payload.email_cc = activityForm.email_cc;
      } else if (activityForm.activity_type === ActivityType.CALL) {
        payload.call_direction = activityForm.call_direction;
        payload.call_result = activityForm.call_result;
        payload.duration_minutes = activityForm.duration_minutes
          ? parseInt(activityForm.duration_minutes) : null;
      } else if (activityForm.activity_type === ActivityType.TASK) {
        payload.task_due_date = activityForm.task_due_date || null;
        payload.task_priority = activityForm.task_priority;
      }

      let response;
      if (editingActivity) {
        response = await axiosInstance.put(
          `/api/crm/activities/${editingActivity.id}`, payload
        );
      } else {
        response = await axiosInstance.post('/api/crm/activities', payload);
      }

      if (response.data.success) {
        setActivityDialogOpen(false);
        resetActivityForm();
        fetchContact();
      }
    } catch (err) {
      console.error('Failed to save activity:', err);
    }
  };

  // Helper functions
  const getFullName = () => {
    if (!contact) return '';
    return [contact.prefix, contact.first_name, contact.middle_name, contact.last_name, contact.suffix]
      .filter(Boolean)
      .join(' ');
  };

  const getInitials = () => {
    if (!contact) return '?';
    const first = contact.first_name?.[0] || '';
    const last = contact.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const getActivityIcon = (type) => {
    const icons = {
      [ActivityType.EMAIL]: <EmailIcon />,
      [ActivityType.CALL]: <CallIcon />,
      [ActivityType.MEETING]: <MeetingIcon />,
      [ActivityType.NOTE]: <NoteIcon />,
      [ActivityType.TASK]: <WorkIcon />,
      [ActivityType.SMS]: <SmsIcon />,
      [ActivityType.CHAT]: <ChatIcon />,
    };
    return icons[type] || <NoteIcon />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!permissionsChecked || loading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        <Alert severity="error">
          You do not have permission to view this contact.
        </Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts`)}
          sx={{ mt: 2 }}
        >
          Back to Contacts
        </Button>
      </Box>
    );
  }

  if (!contact) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        <Alert severity="warning">Contact not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts`)}
          sx={{ mt: 2 }}
        >
          Back to Contacts
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/chatbot/${chatbotId}/crm`)}
          >
            CRM
          </Link>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts`)}
          >
            Contacts
          </Link>
          <Typography color="text.primary">{getFullName()}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts`)}>
              <ArrowBackIcon />
            </IconButton>
            <Avatar
              src={contact.profile_image_url}
              sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.5rem' }}
            >
              {getInitials()}
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {getFullName()}
              </Typography>
              {contact.job_title && (
                <Typography variant="subtitle1" color="text.secondary">
                  {contact.job_title}
                  {contact.account && ` at ${contact.account.name}`}
                </Typography>
              )}
              <Chip
                label={ContactStatusLabels[contact.status]}
                size="small"
                color={ContactStatusColors[contact.status]}
                sx={{ mt: 1 }}
              />
            </Box>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {editMode ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                {canEdit && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                  >
                    Edit
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete
                  </Button>
                )}
                {contact?.email && contact?.contact_type === 'CUSTOMER' && !contact?.is_converted_customer && (
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                    startIcon={<PersonIcon />}
                    onClick={() => setConvertDialogOpen(true)}
                  >
                    Create Customer Account
                  </Button>
                )}
                {contact?.contact_type && (
                  <Chip
                    label={contact.contact_type === 'CUSTOMER' ? 'Customer' : 'Vendor'}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      bgcolor: contact.contact_type === 'CUSTOMER' ? '#d1fae5' : '#e0e7ff',
                      color: contact.contact_type === 'CUSTOMER' ? '#059669' : '#4f46e5',
                    }}
                  />
                )}
              </>
            )}
          </Stack>
        </Box>
      </Box>

      {formErrors.submit && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setFormErrors({})}>
          {formErrors.submit}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Details" />
          <Tab label="Activities" />
          <Tab label="Relationships" />
          <Tab label="Service Reminders" />
        </Tabs>
      </Paper>

      {/* Details Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Contact Type</InputLabel>
                      <Select
                        value={formData.contact_type}
                        label="Contact Type"
                        onChange={handleFormChange('contact_type')}
                      >
                        <MenuItem value=""><em>None</em></MenuItem>
                        <MenuItem value="CUSTOMER">Customer</MenuItem>
                        <MenuItem value="VENDOR">Vendor</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Prefix"
                      value={formData.prefix}
                      onChange={handleFormChange('prefix')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="First Name"
                      value={formData.first_name}
                      onChange={handleFormChange('first_name')}
                      error={!!formErrors.first_name}
                      helperText={formErrors.first_name}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Last Name"
                      value={formData.last_name}
                      onChange={handleFormChange('last_name')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleFormChange('email')}
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Secondary Email"
                      type="email"
                      value={formData.email_secondary}
                      onChange={handleFormChange('email_secondary')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone"
                      value={formData.phone}
                      onChange={handleFormChange('phone')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Mobile"
                      value={formData.phone_mobile}
                      onChange={handleFormChange('phone_mobile')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Work Phone"
                      value={formData.phone_work}
                      onChange={handleFormChange('phone_work')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status}
                        label="Status"
                        onChange={handleFormChange('status')}
                      >
                        {Object.values(ContactStatus).map((status) => (
                          <MenuItem key={status} value={status}>
                            {ContactStatusLabels[status]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Lead Source</InputLabel>
                      <Select
                        value={formData.lead_source}
                        label="Lead Source"
                        onChange={handleFormChange('lead_source')}
                      >
                        <MenuItem value="">None</MenuItem>
                        {Object.values(LeadSource).map((source) => (
                          <MenuItem key={source} value={source}>
                            {LeadSourceLabels[source]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  {contact.contact_type && (
                    <ListItem>
                      <ListItemIcon><PersonIcon /></ListItemIcon>
                      <ListItemText
                        primary={contact.contact_type === 'CUSTOMER' ? 'Customer' : 'Vendor'}
                        secondary="Contact Type"
                      />
                    </ListItem>
                  )}
                  {contact.email && (
                    <ListItem>
                      <ListItemIcon><EmailIcon /></ListItemIcon>
                      <ListItemText primary={contact.email} secondary="Email" />
                    </ListItem>
                  )}
                  {contact.email_secondary && (
                    <ListItem>
                      <ListItemIcon><EmailIcon /></ListItemIcon>
                      <ListItemText primary={contact.email_secondary} secondary="Secondary Email" />
                    </ListItem>
                  )}
                  {contact.phone && (
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText primary={contact.phone} secondary="Phone" />
                    </ListItem>
                  )}
                  {contact.phone_mobile && (
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText primary={contact.phone_mobile} secondary="Mobile" />
                    </ListItem>
                  )}
                  {contact.phone_work && (
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText primary={contact.phone_work} secondary="Work" />
                    </ListItem>
                  )}
                  {contact.lead_source && (
                    <ListItem>
                      <ListItemIcon><PersonIcon /></ListItemIcon>
                      <ListItemText 
                        primary={LeadSourceLabels[contact.lead_source]} 
                        secondary="Lead Source" 
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Professional Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Professional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Job Title"
                      value={formData.job_title}
                      onChange={handleFormChange('job_title')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Department"
                      value={formData.department}
                      onChange={handleFormChange('department')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                          <FormControl fullWidth size="small">
                              <InputLabel>Group</InputLabel>
                              <Select
                                  value={formData.contact_group_id || ''}
                                  label="Group"
                                  onChange={(e) => handleFormChange('contact_group_id')(e)}
                              >
                                  <MenuItem value=""><em>None</em></MenuItem>
                                  {groups.map((g) => (
                                      <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                                  ))}
                              </Select>
                          </FormControl>
                          <IconButton size="small" onClick={() => setManageGroupsOpen(true)}>
                              <SettingsIcon fontSize="small" />
                          </IconButton>
                      </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Account</InputLabel>
                      <Select
                        value={formData.account_id}
                        label="Account"
                        onChange={handleFormChange('account_id')}
                      >
                        <MenuItem value="">None</MenuItem>
                        {accounts.map((account) => (
                          <MenuItem key={account.id} value={account.id}>
                            {account.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  {contact.job_title && (
                    <ListItem>
                      <ListItemIcon><WorkIcon /></ListItemIcon>
                      <ListItemText primary={contact.job_title} secondary="Job Title" />
                    </ListItem>
                  )}
                  {contact.department && (
                    <ListItem>
                      <ListItemIcon><BusinessIcon /></ListItemIcon>
                      <ListItemText primary={contact.department} secondary="Department" />
                    </ListItem>
                  )}
                  {contact.group_rel ? (
                      <ListItem>
                          <ListItemIcon><WorkIcon /></ListItemIcon>
                          <ListItemText primary={contact.group_rel.name} secondary="Group" />
                      </ListItem>
                  ) : contact.contact_group && (
                      // Fallback for old string data
                      <ListItem>
                          <ListItemIcon><WorkIcon /></ListItemIcon>
                          <ListItemText primary={contact.contact_group} secondary="Group" />
                      </ListItem>
                  )}
                  {contact.account && (
                    <ListItem
                      button
                      onClick={() => navigate(`/chatbot/${chatbotId}/crm/accounts/${contact.account.id}`)}
                    >
                      <ListItemIcon><BusinessIcon /></ListItemIcon>
                      <ListItemText primary={contact.account.name} secondary="Account" />
                    </ListItem>
                  )}
                  {contact.date_of_birth && (
                    <ListItem>
                      <ListItemIcon><CakeIcon /></ListItemIcon>
                      <ListItemText 
                        primary={new Date(contact.date_of_birth).toLocaleDateString()} 
                        secondary="Birthday" 
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Address */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Address
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Street"
                      value={formData.street}
                      onChange={handleFormChange('street')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      value={formData.city}
                      onChange={handleFormChange('city')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="State/Province"
                      value={formData.state}
                      onChange={handleFormChange('state')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Postal Code"
                      value={formData.postal_code}
                      onChange={handleFormChange('postal_code')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Country"
                      value={formData.country}
                      onChange={handleFormChange('country')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  {(contact.address?.street || contact.address?.city || contact.address?.country) ? (
                    <ListItem>
                      <ListItemIcon><LocationIcon /></ListItemIcon>
                      <ListItemText
                        primary={[
                            contact.address?.street, 
                            contact.address?.city, 
                            contact.address?.state, 
                            contact.address?.postal_code, 
                            contact.address?.country
                        ].filter(Boolean).join(', ')}
                      />
                    </ListItem>
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      No address on file
                    </Typography>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Social Links */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Social & Web
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Website"
                      value={formData.website}
                      onChange={handleFormChange('website')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="LinkedIn URL"
                      value={formData.linkedin_url}
                      onChange={handleFormChange('linkedin_url')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Twitter Handle"
                      value={formData.twitter_handle}
                      onChange={handleFormChange('twitter_handle')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Facebook URL"
                      value={formData.facebook_url}
                      onChange={handleFormChange('facebook_url')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  {contact.social?.website && (
                    <ListItem button component="a" href={contact.social.website} target="_blank">
                      <ListItemIcon><WebsiteIcon /></ListItemIcon>
                      <ListItemText primary={contact.social.website} />
                    </ListItem>
                  )}
                  {contact.social?.linkedin_url && (
                    <ListItem button component="a" href={contact.social.linkedin_url} target="_blank">
                      <ListItemIcon><LinkedInIcon /></ListItemIcon>
                      <ListItemText primary="LinkedIn" />
                    </ListItem>
                  )}
                  {contact.social?.twitter_handle && (
                    <ListItem button component="a" href={`https://twitter.com/${contact.social.twitter_handle}`} target="_blank">
                      <ListItemIcon><TwitterIcon /></ListItemIcon>
                      <ListItemText primary={`@${contact.social.twitter_handle}`} />
                    </ListItem>
                  )}
                  {contact.social?.facebook_url && (
                    <ListItem button component="a" href={contact.social.facebook_url} target="_blank">
                      <ListItemIcon><FacebookIcon /></ListItemIcon>
                      <ListItemText primary="Facebook" />
                    </ListItem>
                  )}
                  {!contact.social?.website && !contact.social?.linkedin_url && !contact.social?.twitter_handle && !contact.social?.facebook_url && (
                    <Typography color="text.secondary" variant="body2">
                      No social links on file
                    </Typography>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Tags */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tags
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {tags.map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      onClick={() => handleTagToggle(tag.name)}
                      color={formData.tags?.includes(tag.name) ? 'primary' : 'default'}
                      variant={formData.tags?.includes(tag.name) ? 'filled' : 'outlined'}
                      sx={{ 
                        mb: 1,
                        backgroundColor: formData.tags?.includes(tag.name) ? tag.color : undefined,
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {contact.tags?.length > 0 ? (
                    contact.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" />
                    ))
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      No tags assigned
                    </Typography>
                  )}
                </Stack>
              )}
            </Paper>
          </Grid>

          {/* Description */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleFormChange('description')}
                  placeholder="Add notes about this contact..."
                />
              ) : (
                <Typography variant="body2" color={contact.description ? 'text.primary' : 'text.secondary'}>
                  {contact.description || 'No description'}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Activities Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Typography variant="h6">Activity Timeline</Typography>
          {canEdit && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setActivityDialogOpen(true)}
            >
              Log Activity
            </Button>
          )}
        </Box>

        {contact.recent_activities?.length > 0 ? (
          <List>
            {contact.recent_activities.map((activity) => (
              <ListItem key={activity.id} alignItems="flex-start" divider
                secondaryAction={
                  canEdit && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleEditActivity(activity)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteActivityId(activity.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )
                }
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {getActivityIcon(activity.activity_type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1">{activity.subject}</Typography>
                      <Chip
                        label={ActivityTypeLabels[activity.activity_type]}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {activity.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {formatDate(activity.activity_date)}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No activities recorded</Typography>
            {canEdit && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setActivityDialogOpen(true)}
                sx={{ mt: 2 }}
              >
                Log First Activity
              </Button>
            )}
          </Paper>
        )}
      </TabPanel>

      {/* Relationships Tab */}
      <TabPanel value={tabValue} index={2}>
            <ContactRelationships
                contactId={parseInt(contactId)}
                canEdit={canEdit}
                onNavigateToContact={(id) => navigate(`/chatbot/${chatbotId}/crm/contacts/${id}`)}
            />
      </TabPanel>

      {/* Service Reminders Tab */}
      <TabPanel value={tabValue} index={3}>
        <ServiceReminders contactId={parseInt(contactId)} chatbotId={chatbotId} />
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{getFullName()}</strong>?
            This action will archive the contact.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert to Customer Confirmation Dialog */}
      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)}>
        <DialogTitle>Convert Contact to Customer</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            This will create a customer account for <strong>{contact?.first_name} {contact?.last_name}</strong> ({contact?.email}).
          </Typography>
          <Typography variant="body2" color="text.secondary">
            A login credential will be generated and the contact will be able to access the Customer Panel.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
            onClick={handleConvertToCustomer}
            disabled={convertLoading}
          >
            {convertLoading ? <CircularProgress size={20} /> : 'Convert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Result Dialog */}
      <Dialog open={!!convertResult} onClose={() => setConvertResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#10b981' }}>Customer Account Created</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Customer account created successfully.
            {convertResult?.is_new_user
              ? ' A verification email has been sent to the customer.'
              : ' Existing user has been added to the team.'}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            The customer can now access the Customer Panel after verifying their email.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertResult(null)} variant="contained" sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Activity Dialog */}
      <Dialog
        open={activityDialogOpen}
        onClose={() => { setActivityDialogOpen(false); resetActivityForm(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingActivity ? 'Edit Activity' : 'Log Activity'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={activityForm.activity_type}
                  label="Activity Type"
                  onChange={handleActivityFormChange('activity_type')}
                >
                  {Object.values(ActivityType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {ActivityTypeLabels[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={activityForm.subject}
                onChange={handleActivityFormChange('subject')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={activityForm.description}
                onChange={handleActivityFormChange('description')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Date & Time"
                value={activityForm.activity_date}
                onChange={handleActivityFormChange('activity_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Email-specific fields */}
            {activityForm.activity_type === ActivityType.EMAIL && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="From" value={activityForm.email_from}
                    onChange={handleActivityFormChange('email_from')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="To" value={activityForm.email_to}
                    onChange={handleActivityFormChange('email_to')} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="CC" value={activityForm.email_cc}
                    onChange={handleActivityFormChange('email_cc')} />
                </Grid>
              </>
            )}

            {/* Call-specific fields */}
            {activityForm.activity_type === ActivityType.CALL && (
              <>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Direction</InputLabel>
                    <Select value={activityForm.call_direction} label="Direction"
                      onChange={handleActivityFormChange('call_direction')}>
                      <MenuItem value="inbound">Inbound</MenuItem>
                      <MenuItem value="outbound">Outbound</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Result" value={activityForm.call_result}
                    onChange={handleActivityFormChange('call_result')}
                    placeholder="e.g. Connected, Voicemail" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Duration (min)" type="number"
                    value={activityForm.duration_minutes}
                    onChange={handleActivityFormChange('duration_minutes')} />
                </Grid>
              </>
            )}

            {/* Task-specific fields */}
            {activityForm.activity_type === ActivityType.TASK && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth type="datetime-local" label="Due Date"
                    value={activityForm.task_due_date}
                    onChange={handleActivityFormChange('task_due_date')}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select value={activityForm.task_priority} label="Priority"
                      onChange={handleActivityFormChange('task_priority')}>
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="critical">Critical</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setActivityDialogOpen(false); resetActivityForm(); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddActivity}
            disabled={!activityForm.subject}
          >
            {editingActivity ? 'Update Activity' : 'Log Activity'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Activity Confirmation */}
      <Dialog open={Boolean(deleteActivityId)} onClose={() => setDeleteActivityId(null)}>
        <DialogTitle>Delete Activity</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this activity? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteActivityId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDeleteActivity(deleteActivityId)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <ContactGroupManager 
          open={manageGroupsOpen} 
          onClose={() => setManageGroupsOpen(false)}
          onChange={() => { fetchGroups(); fetchContact(); }}
      />
    </Box>
  );
};

export default ContactDetail;